<template>
  <b-modal
    id="modal-upload-facerecogntion"
    ref="modalUploadFaceRecognition"
    size="md"
    centered
    :title="isUploading ? 'Uploading...' : $t('rfidAndFacerecognition.upload.title')"
    @hidden="resetState"
  >
    <b-card class="mc-card mc-card__setting mc-card__setting--upload-face-recognition">
      <div class="d-flex justify-content-center align-items-center">
        <img :src="UploadGuideline" alt="Upload Guideline" />
      </div>
      <p class="text-muted text-center mt-3">
        {{ $t('rfidAndFacerecognition.upload.subtitle') }}
      </p>

      <div v-if="!isUploading" class="upload-container">
        <FileUploader
          :data-me="`master-fr-modal-dropzone-upload-${employee?.userId}`"
          :title="$t('rfidAndFacerecognition.upload.chooseOrDropFile')"
          :subtitle="$t('rfidAndFacerecognition.upload.fileType')"
          :buttonText="$t('rfidAndFacerecognition.upload.chooseFile')"
          accept=".jpg,.png"
          :maxSize="1 * 1024 * 1024"
          @selected="handleSelectedFile"
          @error="handleError"
        />
      </div>

      <div v-else class="upload-progress-view">
        <p class="file-name text-center">{{ file ? file.name : '-' }}</p>
        <b-row>
          <b-col sm="12">
            <b-progress
              :data-me="`master-fr-modal-dropzone-progress-${employee?.userId}`"
              :value="uploadProgress"
              variant="primary"
              class="mt-2 custom-progress"
            />
          </b-col>
        </b-row>
        <div class="d-flex justify-content-between align-items-center">
          <span class="file-size text-muted">
            {{ formatSize(uploadedSize) }} / {{ formatSize(totalSize) }}
          </span>
          <span class="progress-percentage mr-3">{{ uploadProgress }} %</span>
        </div>
      </div>
    </b-card>

    <template #modal-footer="{ cancel }">
      <b-row class="mc-modal__filter-footer mx-0">
        <b-col sm="12" md="12" class="mc-modal__filter-footer-btn mt-0">
          <McButton
            :data-me="`master-fr-btn-close-modal-upload-${employee?.userId}`"
            :disabled="isUploading"
            variant="outline-primary"
            class="me-2"
            :text="$t('global.action.close')"
            @click="cancel()"
          />
          <McButton
            :data-me="`master-fr-btn-upload-${employee?.userId}`"
            variant="primary"
            :disabled="!file"
            :loading="isUploading"
            :text="$t('global.action.apply')"
            @click="processFile"
          />
        </b-col>
      </b-row>
    </template>
  </b-modal>
</template>

<script>
import McButton from '@/components/McButton'
import FileUploader from '@/router/views/master/rfid-facerecognition/components/FileUploader.vue'
import { mapGetters } from 'vuex'
import { Helper } from '@/mixin/helper'
import UploadGuideline from '@/assets/images/face-recognition/upload-guideline.jpg'
import { UPDATE_EMPLOYEE } from '@/services/api/master/employees'

const ALLOWED_FILE_TYPES = ['image/jpeg', 'image/png']
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png']
const SIMULATE_INTERVAL_MS = 200
const SIMULATE_STEP = 5
const SIMULATE_MAX = 90
const HIDE_DELAY_MS = 800

export default {
  name: 'ModalUploadFaceRecognition',

  components: { McButton, FileUploader },

  mixins: [Helper],

  data() {
    return {
      UploadGuideline,
      employee: null,
      file: null,
      fileExt: null,
      isUploading: false,
      uploadProgress: 0,
      uploadedSize: 0,
      totalSize: 0,
      uploadInterval: null,
    }
  },

  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
    }),
  },

  methods: {
    show(employee) {
      this.employee = employee
      this.$bvModal.show('modal-upload-facerecogntion')
    },

    hide() {
      this.$bvModal.hide('modal-upload-facerecogntion')
    },

    handleSelectedFile(file) {
      this.file = file
      this.fileExt = this.getFileExtension(file)
    },

    handleError(msg) {
      this.$mcToast.error(msg)
    },

    processFile() {
      if (!this.file) return

      if (!this.validateFileType(this.file)) {
        this.$mcToast.error(
          `Invalid file type. Only ${ALLOWED_EXTENSIONS.join(', ')} files are allowed.`
        )
        return
      }

      this.isUploading = true
      this.totalSize = this.file.size
      this.uploadedSize = 0
      this.uploadProgress = 0

      this.performUpload()
    },

    async performUpload() {
      this.simulateProgress()

      try {
        const base64Photo = await this.fileToBase64(this.file)
        const splitBase64Photo = base64Photo.split(',')[1]
        const vehicleIds = this.employee?.vehicles ?? []

        const { data } = await this.$apollo.mutate({
          mutation: UPDATE_EMPLOYEE,
          variables: {
            input: {
              userId: this.employee.userId,
              newData: {
                photo: splitBase64Photo,
                photoExt: this.fileExt,
                identification: {
                  vehicleIds,
                  rfid: this.employee?.rfid?.rfid,
                },
              },
              oldData: {
                photograph: this.employee.photograph ?? null,
                identification: {
                  vehicleIds,
                  rfid: this.employee?.rfid?.rfid ?? null,
                },
              },
            },
          },
        })

        const result = data?.updateEmployee

        if (!result?.isSuccessful) {
          throw new Error(result?.message || 'Upload failed')
        }

        this.stopSimulateProgress()
        this.uploadProgress = 100
        this.uploadedSize = this.totalSize

        this.$emit('success', {
          userId: result?.employee?.userId,
          file: this.file,
        })
        this.$mcToast.success('Upload success')

        setTimeout(() => this.hide(), HIDE_DELAY_MS)
      } catch (error) {
        this.stopSimulateProgress()
        this.$mcToast.error(error?.message || 'Upload failed')
        this.hide()
      }
    },

    simulateProgress() {
      this.uploadInterval = setInterval(() => {
        if (this.uploadProgress < SIMULATE_MAX) {
          this.uploadProgress += SIMULATE_STEP
          this.uploadedSize = (this.uploadProgress / 100) * this.totalSize
        }
      }, SIMULATE_INTERVAL_MS)
    },

    stopSimulateProgress() {
      clearInterval(this.uploadInterval)
      this.uploadInterval = null
    },

    validateFileType(file) {
      const mimeValid = ALLOWED_FILE_TYPES.includes(file.type)
      const ext = this.getFileExtension(file).toLowerCase()
      return mimeValid && ALLOWED_EXTENSIONS.includes(ext)
    },

    getFileExtension(file) {
      return file.name.split('.').pop() ?? ''
    },

    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => resolve(reader.result)
        reader.onerror = (error) => reject(error)
      })
    },

    formatSize(bytes) {
      if (bytes === 0) return '0 MB'
      return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
    },

    resetState() {
      this.stopSimulateProgress()
      this.isUploading = false
      this.file = null
      this.fileExt = null
      this.uploadProgress = 0
      this.uploadedSize = 0
      this.totalSize = 0
      this.employee = null
    },
  },
}
</script>
